import { useFilterContext } from "context/FilterContext";
import { format } from "date-fns";
import { dropdownOptions } from "pages/Content/Management/ContentForm";
import { radioButtonOptions } from "pages/ScheduledForms/AddOrEditScheduledForm";
import { getFormSpecificName, getStudySpecificName } from "pages/ScheduledForms/ScheduledFormsTable";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

export function useFilter(initialData, tableHeaders) {
  const [data, setData] = useState(initialData);
  const [addFilters, setAddFilters] = useState([]);
  const [badgeCount, setBadgeCount] = useState();
  const { selectedFilter, setSelectedFilter, searchTerm, setSearchTerm } =
    useFilterContext();

    const {
      data: studyData,
      loading: studyLoading,
      error: studyError,
    } = useSelector(({ study }) => study);
  

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const handleSearchTerm = (event) => {
    setSearchTerm(event.target.value);
    setBadgeCount(1);
  };

  const handleApplyFilters = (newFilters) => {
    setAddFilters(newFilters);
  };

  const MAX_FILTERS = Math.max(1, tableHeaders.length - 1);

  const addNewFilter = () => {
    if (addFilters.length < MAX_FILTERS) {
      const newFilters = [
        ...addFilters,
        { selectedFilter: "", searchTerm: "" },
      ];
      setAddFilters(newFilters);
      return newFilters;
    }
  };

  const removeAddFilters = (index) => {
    const updatedFilters = [...addFilters];
    updatedFilters.splice(index, 1);
    setAddFilters(updatedFilters);
  };

  const handleAddFilterSelectedFilter = (e, index) => {
    const { value } = e.target;
    setAddFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      updatedFilters[index].selectedFilter = value;
      return updatedFilters;
    });
  };

  const handleAddFilterSearchTerm = (e, index) => {
    const { value } = e.target;
    setAddFilters((prevFilters) => {
      const updatedFilters = [...prevFilters];
      updatedFilters[index].searchTerm = value;
      return updatedFilters;
    });
  };

  const normalizeText = (text) =>
    text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  function matchesSearchTerm(text, searchTerm) {
    const normalizedText = normalizeText(text.toString());
    const normalizedSearchTerm = normalizeText(searchTerm);
    return normalizedText.includes(normalizedSearchTerm);
  }
  function filterByBooleanOrStatus(itemToFilter, searchTerm, filterKey) {
    const searchTermBool = ["a", "s", "h"].includes(
      searchTerm.toLowerCase().charAt(0)
    );

    let contentValue;
    if (filterKey === "status") {
      contentValue = itemToFilter[filterKey]?.toLowerCase() === "active";
    } else {
      contentValue = !!itemToFilter[filterKey];
    }

    return contentValue === searchTermBool;
  }

  function filterByAuthor(itemToFilter, searchTerm, filterKey) {
    const authorName =
      filterKey === "author"
        ? `${itemToFilter.createdBy?.firstName} ${itemToFilter.createdBy?.lastName}`.toLowerCase()
        : `${itemToFilter.sender?.firstName} ${itemToFilter.sender?.lastName}`.toLowerCase();

    return matchesSearchTerm(authorName, searchTerm);
  }

  function filterByDate(itemToFilter, searchTerm, filterKey) {
    const dateValue = itemToFilter[filterKey];

    if (!Date.parse(dateValue)) {
      return false;
    }
    const dateType = format(new Date(dateValue), "dd/MM/yyyy");

    return matchesSearchTerm(dateType, searchTerm);
  }

  function filterByAppAndBusinessOptions(itemToFilter, searchTerm, filterKey) {
    const optionValue =
      filterKey === "app"
        ? itemToFilter.app?.appName
          ? itemToFilter.app.appName.toLowerCase()
          : "-"
        : itemToFilter.businessPackage?.name
        ? itemToFilter.businessPackage.name.toLowerCase()
        : "-";

    return matchesSearchTerm(optionValue, searchTerm);
  }

  function filterByFormName(itemToFilter, searchTerm, filterKey) {
    const formName = getFormSpecificName(itemToFilter, studyData);
    return matchesSearchTerm(formName, searchTerm);
  }

  function filterByStudyName(itemToFilter, searchTerm, filterKey) {
    const formName = getStudySpecificName(itemToFilter, studyData);
    return matchesSearchTerm(formName, searchTerm);
  }

  function filterByTypeLabel(itemToFilter, searchTerm, filterKey) {
    const typeLabel = radioButtonOptions.find(option => option.value === itemToFilter[filterKey])?.label;
    return matchesSearchTerm(typeLabel, searchTerm);
  }



  function filterByWizardTitle(itemToFilter, searchTerm) {
    const wizardTitles = itemToFilter.wizards.map(wizard => wizard.title.toLowerCase());
    const normalizedSearchTerm = normalizeText(searchTerm);
    return wizardTitles.some(title => title.includes(normalizedSearchTerm));
  }

  const filterStrategies = {
    url: (itemToFilter, searchTerm) => {
      const hasImage = Boolean(itemToFilter["url"]);
      return searchTerm.toLowerCase() === "si" ||
        searchTerm.toLowerCase() === "s"
        ? hasImage
        : !hasImage;
    },
    contentType: (itemToFilter, searchTerm) => {
      const contentTypeValue = itemToFilter["contentType"];
      const contentTypeName = dropdownOptions.find(
        (option) => option.value === contentTypeValue
      );
      if (!contentTypeName) return false;
      return matchesSearchTerm(contentTypeName.name, searchTerm);
    },
    enabled: (itemToFilter, searchTerm) =>
      filterByBooleanOrStatus(itemToFilter, searchTerm, "enabled"),
    visited: (itemToFilter, searchTerm) =>
      filterByBooleanOrStatus(itemToFilter, searchTerm, "visited"),
    status: (itemToFilter, searchTerm) =>
      filterByBooleanOrStatus(itemToFilter, searchTerm, "status"),
    author: (itemToFilter, searchTerm) =>
      filterByAuthor(itemToFilter, searchTerm, "author"),
    authorMessage: (itemToFilter, searchTerm) =>
      filterByAuthor(itemToFilter, searchTerm, "authorMessage"),
    noteDate: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "noteDate"),
    created: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "created"),
    createdDate: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "createdDate"),
    startDate: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "startDate"),
    endDate: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "endDate"),
    birthDay: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "birthDay"),
    email: (itemToFilter, searchTerm) => {
      const email = itemToFilter.user.email.toLowerCase();
      return matchesSearchTerm(email, searchTerm);
    },
    height: (itemToFilter, searchTerm) => {
      const height = itemToFilter["height"]
        ? itemToFilter[selectedFilter].toString()
        : "-";
      const searchTermString = searchTerm?.toString() ?? "";

      return matchesSearchTerm(height, searchTermString);
    },
    sex: (itemToFilter, searchTerm) => {
      const sexMap = {
        woman: "mujer",
        man: "hombre",
        other: "otros",
      };

      const sexValue =
        sexMap[itemToFilter["sex"]?.toLowerCase()] ??
        itemToFilter["sex"]?.toLowerCase();
      const searchTermString = searchTerm?.toLowerCase() ?? "";

      return matchesSearchTerm(sexValue ? sexValue : "", searchTermString);
    },
    app: (itemToFilter, searchTerm) =>
      filterByAppAndBusinessOptions(itemToFilter, searchTerm, "app"),
    businessPackage: (itemToFilter, searchTerm) =>
      filterByAppAndBusinessOptions(
        itemToFilter,
        searchTerm,
        "businessPackage"
      ),
    score: (itemToFilter, searchTerm) => {
      const score = itemToFilter["score"]
        ? itemToFilter[selectedFilter].toString()
        : "-";
      const searchTermString = searchTerm?.toString() ?? "";

      return matchesSearchTerm(score, searchTermString);
    },
    linkVisited: (itemToFilter, searchTerm) =>
      filterByBooleanOrStatus(itemToFilter, searchTerm, "linkVisited"),
    firstVisitDate: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "firstVisitDate"),
    firstLogin: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "firstLogin"),
    lastAccess: (itemToFilter, searchTerm) =>
      filterByDate(itemToFilter, searchTerm, "lastAccess"),
    slug: (itemToFilter, searchTerm) =>
      filterByFormName(itemToFilter, searchTerm, 'slug'),
    studiesFormId: (itemToFilter, searchTerm) =>
      filterByStudyName(itemToFilter, searchTerm, 'studiesFormId'),
    type: (itemToFilter, searchTerm) => 
      filterByTypeLabel(itemToFilter, searchTerm, 'type'),
    wizards: (itemToFilter, searchTerm) => 
      filterByWizardTitle(itemToFilter, searchTerm, 'wizards'),
  };

  const filteredData = data.filter((itemToFilter) => {
    if (filterStrategies[selectedFilter]) {
      return filterStrategies[selectedFilter](itemToFilter, searchTerm);
    } else {
      const filterValue = itemToFilter[selectedFilter]
        ? itemToFilter[selectedFilter]?.toString().toLowerCase()
        : "-";

      const searchTermString = searchTerm?.toString().toLowerCase() ?? "";

      // * This option is to add additional filters
      if (selectedFilter && searchTerm && addFilters.length > 0) {
        const addFilterResult = addFilters.every((filter) => {
          const filterData = itemToFilter[filter.selectedFilter]?.toLowerCase();
          return (
            filterData && filterData.includes(filter.searchTerm.toLowerCase())
          );
        });

        return addFilterResult;
      }

      if (selectedFilter && searchTerm && !addFilters.length) {
        return filterValue.includes(searchTermString);
      }

      if (!selectedFilter && !searchTerm) {
        return true;
      }

      return false;
    }
  });

  const removeFilters = () => {
    setSearchTerm("");
    setSelectedFilter("");
    setBadgeCount();
    setAddFilters([]);
  };

  useEffect(() => {
    return () => {
      setSearchTerm("");
      setSelectedFilter("");
      setBadgeCount();
      setAddFilters([]);
    };
  }, []);

  return {
    addFilters,
    handleFilterChange,
    handleSearchTerm,
    handleApplyFilters,
    addNewFilter,
    removeAddFilters,
    handleAddFilterSelectedFilter,
    handleAddFilterSearchTerm,
    filteredData,
    removeFilters,
    badgeCount,
    MAX_FILTERS,
  };
}
